import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { CircleOutlined, RectangleOutlined } from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useAtomValue } from 'jotai';
import { xor } from 'lodash';
import { useParams } from 'react-router';

import { RBLIcon } from '../../../../../common-components/elements/icon/svgs/RBLIcon';
import { VideaToothParts } from '../../../../../common-components/elements/VideaIcons/VideaIcons';
import { usePreferences } from '../../../../../react-query/usePreferences';
import {
  blueBlack630,
  blueBlack640,
  blueBlack700,
  orange100,
  red400,
  white,
  yellow500,
} from '../../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../../utils/mixpanel';
import {
  casePresentationModeAtom,
  selectedFindingsAtom,
} from '../../../casePresentation/atoms';
import { toothPartsTogglesAtom } from '../../../findingSidebar/atoms';
import type { FindingTypes, FindingsCategory } from '../../../types';
import { FINDINGS_CATEGORIES } from '../../../types';
import { useGetModelFeatureFlags } from '../../../utils';
import { showSegmentationAtom } from '../atoms';

const ToggleGroup = styled(ToggleButtonGroup)`
  color: ${white};
  column-gap: 8px;
  padding: 0;
  margin-top: 0.25em;
  border-radius: 0.3em;
`;

const ToggleButtonStyled = styled(ToggleButton)`
  border: 0 !important;
  background-color: ${blueBlack700};
  border-radius: 0.3em;
  padding: 0;
  margin-left: 0 !important;

  &.Mui-selected {
    background-color: ${blueBlack640};
    border-radius: 0.3em;
    margin-left: 0 !important;
    border: 0 !important;
  }

  &.Mui-selected:hover,
  &:hover {
    background-color: ${blueBlack630};
  }

  &.MuiToggleButtonGroup-grouped {
    margin: 0;
    border: 0;
    border-radius: 0.26em;
    width: 60px;
    height: 32px;
    &:not(:first-of-type) {
      border-radius: 0.28em;
    }
    &:first-of-type {
      border-radius: 0.28em;
    }
  }
`;

const RestorativeIcon = styled(RectangleOutlined)`
  fill: ${red400};
`;

const IncipientIcon = styled(RectangleOutlined)`
  fill: ${yellow500};
  font-size: 18px;
`;

const PeriodontalIcon = styled.div`
  display: flex;
`;

const EndodonticIcon = styled(CircleOutlined)`
  fill: ${orange100};
  font-size: 18px;
`;

const IconWrapper = styled.div`
  padding: 11px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const FindingsToggles = ({
  enabledFindingTypes,
  onSetEnabledFindingTypes,
}: {
  onSetEnabledFindingTypes: React.Dispatch<React.SetStateAction<FindingTypes>>;
  enabledFindingTypes: FindingTypes;
}) => {
  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
    isAnatomyEnabled,
  } = useGetModelFeatureFlags();
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const { trackEvent } = useTrackEvent();
  const preferencesQuery = usePreferences();
  const isSegmentationEnabled = Boolean(
    preferencesQuery?.data?.segmentationEnabled,
  );

  const isCasePresentationMode = useAtomValue(casePresentationModeAtom);
  const singleFinding = useAtomValue(selectedFindingsAtom);
  const { t } = useLingui();

  const isPerioDisabled =
    !isRblEnabled ||
    (isCasePresentationMode &&
      singleFinding &&
      singleFinding?.type !== 'rbl' &&
      singleFinding?.type !== 'calculus');

  const isEndoDisabled =
    !isPrlEnabled ||
    (isCasePresentationMode &&
      singleFinding &&
      singleFinding?.type !== 'periapical_radiolucency');

  const isRestoDisabled =
    isCasePresentationMode &&
    singleFinding &&
    singleFinding?.type !== 'caries' &&
    singleFinding?.type !== 'restorative_adjacent_lucency';

  const showSegmentation = useAtomValue(showSegmentationAtom);
  const toothPartsToggles = useAtomValue(toothPartsTogglesAtom);

  if (preferencesQuery?.data?.useSidebarRedesign) {
    return null;
  }

  const selectedFindingsCategories: FindingsCategory[] =
    FINDINGS_CATEGORIES.filter(
      (category) =>
        (category === 'restorative' &&
          enabledFindingTypes.caries &&
          !isRestoDisabled) ||
        (category === 'incipients' &&
          enabledFindingTypes.incipients &&
          !isRestoDisabled) ||
        (category === 'periodontal' &&
          enabledFindingTypes.radiographic_boneloss &&
          !isPerioDisabled) ||
        (category === 'endodontic' &&
          enabledFindingTypes.periapical_radiolucency &&
          !isEndoDisabled) ||
        (category === 'chart' && toothPartsToggles.tooth_anatomy),
    );
  const handleToggleClick = (newList: FindingsCategory[]) => {
    const newFindingsTypes: FindingTypes = {
      ...enabledFindingTypes,
      caries: newList.includes('restorative'),
      incipients: newList.includes('incipients'),
      area_of_concern:
        isCariesSubclassEnabled && newList.includes('restorative'),
      restorative_adjacent_lucency:
        isVdaEnabled && newList.includes('restorative'),
      radiographic_boneloss: isRblEnabled && newList.includes('periodontal'),
      calculus: isCalculusEnabled && newList.includes('periodontal'),
      periapical_radiolucency: isPrlEnabled && newList.includes('endodontic'),
    };
    onSetEnabledFindingTypes(newFindingsTypes);
    // Find which category was toggled by comparing the new list of categories to the old one
    // There should only be one difference, which is our changed category
    const toggledCategory = xor(newList, selectedFindingsCategories)[0];
    if (toggledCategory === 'chart') {
      toothPartsToggles.tooth_anatomy = !toothPartsToggles.tooth_anatomy;
    }
    trackEvent(mixpanelEvents.toggleFindings, {
      toggled: {
        category: toggledCategory,
        // if the new list includes the toggled value, then the toggled category was toggled on
        value: newList.includes(toggledCategory),
      },
      previousFindingsTypes: enabledFindingTypes,
      newFindingsTypes: newFindingsTypes,
      patientId: selectedPatientId,
    });
  };

  return (
    <ToggleGroup
      onChange={(_, value) => handleToggleClick(value)}
      value={selectedFindingsCategories}
      className="sentry-unmask"
    >
      <ToggleButtonStyled value="restorative" disabled={isRestoDisabled}>
        <Tooltip title={t`Restorative`}>
          <IconWrapper>
            <RestorativeIcon />
          </IconWrapper>
        </Tooltip>
      </ToggleButtonStyled>
      <ToggleButtonStyled value="incipients" disabled={isRestoDisabled}>
        <Tooltip title={t`Incipient`}>
          <IconWrapper>
            <IncipientIcon />
          </IconWrapper>
        </Tooltip>
      </ToggleButtonStyled>
      <ToggleButtonStyled
        value="periodontal"
        disabled={!isRblEnabled || isPerioDisabled}
      >
        <PeriodontalIcon>
          <Tooltip title={t`Periodontal`}>
            <IconWrapper>
              <RBLIcon />
            </IconWrapper>
          </Tooltip>
        </PeriodontalIcon>
      </ToggleButtonStyled>
      <ToggleButtonStyled
        value="endodontic"
        disabled={!isPrlEnabled || isEndoDisabled}
      >
        <Tooltip title={t`Endodontic`}>
          <IconWrapper>
            <EndodonticIcon />
          </IconWrapper>
        </Tooltip>
      </ToggleButtonStyled>
      {!!showSegmentation && isSegmentationEnabled && (
        <ToggleButtonStyled
          value="chart"
          disabled={!isAnatomyEnabled}
          
        >
          <Tooltip title={t`Tooth Anatomy`}>
            <IconWrapper>
              <VideaToothParts />
            </IconWrapper>
          </Tooltip>
        </ToggleButtonStyled>
      )}
    </ToggleGroup>
  );
};
